import type { PhaseQuestionDto } from './fase0';
import type { PhaseCommonDto } from './fasi';

/**
 * La fase 10 «La diagnosi» del contratto n. 1 versione 2.2 (art. 8, fase 10; art. 2-bis; art. 3.40-3.41; ADR 0179), nella
 * forma della pagina di prova `docs/mockup/percorso-sinapsi/13-fase10`: una riga per criterio, gli scarti delle fasi 2-9
 * per reparto sotto i tre criteri, il confronto con il settore, le domande «[voce] oggi c'è?», il registro intero.
 * Le parti comuni alle fasi 10 e 11 (`ClosingCommonDto`) estendono `PhaseCommonDto` dell'ossatura (ADR 0174).
 */

/** I tre criteri dell'azienda al suo meglio, nell'ordine in cui vincono nei conflitti (art. 2-bis). */
export const CRITERIA = ['in_regola', 'ben_fatta', 'nella_sua_misura'] as const;
export type Criterion = (typeof CRITERIA)[number];
export const CRITERION_TEXT: Record<Criterion, string> = { in_regola: 'In regola', ben_fatta: 'Ben fatta', nella_sua_misura: 'Nella sua misura' };

/** Il grado di uno scarto nella diagnosi (art. 8, fase 10; art. 3.40): certo «non c'è», probabile «le fonti non lo mostrano», superato «c'è». */
export const SCRAP_CERTAINTIES = ['certo', 'probabile', 'superato'] as const;
export type ScrapCertainty = (typeof SCRAP_CERTAINTIES)[number];

/** Quanto pesa uno scarto: il consulente dice quali pesano di più (art. 8, fase 10, consulente). */
export const SCRAP_WEIGHTS = ['alto', 'medio', 'basso'] as const;
export type ScrapWeight = (typeof SCRAP_WEIGHTS)[number];

/** Le risposte chiuse della fase 10, che coprono ogni caso (art. 7-bis.4): la predefinita è «non c'è». */
export const DIAGNOSIS_ANSWERS = ['c_e', 'non_c_e', 'in_parte'] as const;
export type DiagnosisAnswer = (typeof DIAGNOSIS_ANSWERS)[number];

/** Una voce del registro di una fase prima che aiuterebbe qui (art. 7-bis.13): non è una domanda nuova. */
export interface ClosingHelpDto {
  domanda_id: string;
  voce: string;
  fase: number;
  stato: PhaseQuestionDto['stato'];
  cosa_cambierebbe: string;
}

/**
 * Le parti comuni delle pagine delle fasi 10 e 11: quelle di ogni fase (`PhaseCommonDto`, ADR 0174) più ciò che le fasi
 * 10 e 11 mostrano in quanto riepilogo di «La tua azienda»: il tempo massimo dell'incarico, il registro intero e le
 * «cose che aiuterebbero qui».
 */
export interface ClosingCommonDto extends PhaseCommonDto {
  fase: 10 | 11;
  /** Il tempo massimo dell'ondata E e dell'incarico, con le sue sedi (art. 10.3). */
  ondata: PhaseCommonDto['ondata'] & { incarico_s: number };
  /** Il registro intero (art. 7-bis.11): le fasi 10 e 11 sono «La tua azienda», che lo mostra con i cinque stati. */
  registro_intero: PhaseQuestionDto[];
  /** Le voci del registro di fasi prima che cambierebbero questa fase (art. 7-bis.13). */
  aiuterebbero: ClosingHelpDto[];
}

/** Uno scarto della diagnosi, con la voce delle fasi 2-9 da cui nasce (art. 8, fase 10, consegna e bloccanti). */
export interface DiagnosisScrapDto {
  /** Il numero dello scarto nella diagnosi, lo stesso nella fase 11 («scarto 3»). */
  n: number;
  /** Lo scarto come lo riconosce lo studio (`<fase>:<id della voce>`). */
  id: string;
  criterio: Criterion;
  fase: number;
  /** La voce da cui nasce, con il collegamento alla sua pagina di fase. */
  voce: { id: string; nome: string; fase: number };
  testo: string;
  peso: ScrapWeight;
  certezza: ScrapCertainty;
  /** La fonte o la risposta da cui si sa (per «certo» e «superato»), in parole. */
  fonte: string | null;
  /** Le altre sedi, quando lo scarto tocca reparti di sedi diverse: sta una volta. */
  vale_anche_per: string[];
  /** La nota del titolare («in parte»), o la scelta di Timonio. */
  nota: string | null;
  /** Il perché del consulente, dove non ce n'è nessuno nella fase da cui viene. */
  perche: string | null;
}

export interface DiagnosisDepartmentDto {
  reparto: string;
  sede: string;
  scarti: DiagnosisScrapDto[];
  /** Il perché quando il reparto non ha scarti (art. 8, fase 10, soglie). */
  perche_nessuno: string | null;
}

/** Una riga per criterio: dove l'azienda di oggi sta rispetto a quella al suo meglio (art. 8, fase 10, consegna). */
export interface DiagnosisCriterionDto {
  criterio: Criterion;
  dove_sta: string;
  certi: number;
  probabili: number;
  superati: number;
}

/** Il confronto con il settore, preso dallo studio dai conti letti della fase 1, senza ricalcolarlo (art. 3.41). */
export interface SectorComparisonDto {
  rapporto: string;
  azienda: string;
  /** Il calcolo sotto il numero dell'azienda («2.610.000 / 18»), se c'è. */
  calcolo: string | null;
  settore: string;
  fonte: string | null;
}

export interface PhaseTenDto extends ClosingCommonDto {
  fase: 10;
  numeri: { scarti: number; certi: number; probabili: number; superati: number; reparti: number; reparti_per_sede: string };
  criteri: DiagnosisCriterionDto[];
  reparti: DiagnosisDepartmentDto[];
  settore: SectorComparisonDto[];
  /** Le note della pagina sotto i reparti: «vale anche per», le fasi 2-3 sotto «in regola», i «perché no». */
  note: string[];
  /** Quanti scarti probabili non sono stati chiesti (restano probabili e dichiarati). */
  non_chiesti: number;
}
